<template>
  <div
    data-state="active"
    data-orientation="horizontal"
    role="tabpanel"
    aria-labelledby="radix-_r_4m_-trigger-rules"
    id="radix-_r_4m_-content-rules"
    tabindex="0"
    class="flex flex-col mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-full overflow-auto space-y-4"
  >
    <div
      class="flex-1 flex flex-col rounded-lg border bg-card text-card-foreground shadow-2xs"
      data-v0-t="card"
    >
      <div class="flex-1 flex flex-col p-6 relative overflow-hidden">
        <el-table
          class="flex-1"
          v-loading="tableCtrl.state.loading"
          element-loading-background="rgba(0, 0, 0, 0.8)"
          :data="tableCtrl.state.tableData"
          border
          height="auto"
          style="width: 100%"
        >
          <el-table-column prop="ruleName" label="Rule Name" />
          <el-table-column prop="assetNames" label="Event Source" />
          <el-table-column prop="eventLevels" label="Levels">
            <template #default="{ row }">
              <div
                v-if="parseEventLevels(row.eventLevels).length"
                class="flex flex-wrap gap-2"
              >
                <RenderEventLogLevel
                  v-for="level in parseEventLevels(row.eventLevels)"
                  :key="`${row.id}-${level}`"
                  :model-value="level"
                />
              </div>
              <span v-else>--</span>
            </template>
          </el-table-column>
          <el-table-column prop="cooldownMin" label="Cooldown" />
          <el-table-column prop="recipientNames" label="Recipients" />
          <el-table-column prop="enabled" label="Status">
            <template #default="scope">
              <div
                class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
                :class="{
                  'bg-green-100 text-green-800': scope.row.enabled === 1,
                  'bg-red-100 text-red-800': scope.row.enabled === 0,
                }"
              >
                {{ scope.row.enabled === 1 ? "Enabled" : "Disabled" }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Actions">
            <template #default="scope">
              <ActionButtons
                @command="handleCommand"
                :actions="actions"
                :item="scope.row"
              />
            </template>
          </el-table-column>
        </el-table>
        <div class="flex mt-4 justify-end">
          <el-pagination
            @size-change="(val) => tableCtrl.setPageSize(val)"
            @current-change="(val) => tableCtrl.setCurrentPage(val)"
            :current-page="tableCtrl.state.pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="tableCtrl.state.pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableCtrl.state.pagination.total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TableController } from "@/controllers/TableController";
import {
  listNotificationRules,
  deleteNotificationRule,
  testNotificationTemplate,
} from "@/api";
import ActionButtons from "@/components/ActionButtons.vue";
import RenderEventLogLevel from "@/components/RenderEventLogLevel.vue";
import { Loading } from "element-ui";
const normalizeSearchValue = (value) => {
  const str = typeof value === "string" ? value.trim() : "";
  return str || undefined;
};
const normalizeStatusFilter = (value) => {
  if (value === undefined || value === null || value === "" || value === "all") {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

export default {
  name: "AlertRules",
  components: { ActionButtons, RenderEventLogLevel },
  props: {
    searchText: {
      type: String,
      default: "",
    },
    statusFilter: {
      type: [Number, String],
      default: undefined,
    },
  },
  data() {
    return {
      tableCtrl: new TableController({
        loadMethod: listNotificationRules,
        autoLoad: true,
        isClientPaged: true, // Client-side pagination
        pageSize: 10,
        keyField: "id",
        queryParameters: {
          search: normalizeSearchValue(this.searchText),
          enabled: normalizeStatusFilter(this.statusFilter),
        },
      }),
    };
  },
  watch: {
    searchText(value) {
      this.applyServerSearch(value);
    },
    statusFilter(value) {
      this.applyStatusFilter(value);
    },
  },
  computed: {
    actions() {
      return [
        {
          type: "dropdown",
          label: "more",
          icon: "el-icon-more",
          items: [
            { label: "Edit Rule", command: "edit", icon: "el-icon-edit" },
            { label: "View Details", command: "view", icon: "el-icon-view" },
            {
              label: "Test Alert",
              command: "test",
              icon: "el-icon-s-promotion",
            },
            {
              label: "Delete Rule",
              command: "delete",
              icon: "el-icon-delete",
              color: "#F56C6C",
              divided: true,
            },
          ],
        },
      ];
    },
  },
  methods: {
    parseEventLevels(rawLevels) {
      const toNumeric = (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
      };
      if (rawLevels === undefined || rawLevels === null || rawLevels === "") {
        return [];
      }
      if (Array.isArray(rawLevels)) {
        return rawLevels
          .map((level) => toNumeric(level))
          .filter((level) => level !== null);
      }
      return String(rawLevels)
        .split(/[,，]/)
        .map((value) => toNumeric(value.trim()))
        .filter((level) => level !== null);
    },
    applyServerSearch(value) {
      if (!this.tableCtrl) {
        return;
      }
      const normalized = normalizeSearchValue(value);
      if (this.tableCtrl.queryParameters.search === normalized) {
        return;
      }
      this.tableCtrl.queryParameters.search = normalized;
      this.tableCtrl.refresh();
    },
    applyStatusFilter(value) {
      if (!this.tableCtrl) {
        return;
      }
      const normalized = normalizeStatusFilter(value);
      if (this.tableCtrl.queryParameters.enabled === normalized) {
        return;
      }
      this.tableCtrl.queryParameters.enabled = normalized;
      this.tableCtrl.refresh();
    },
    mockDataList() {
      return new Promise((resolve) =>
        setTimeout(
          () =>
            resolve(
              Array.from({ length: 20 }).map((_, i) => ({
                id: 1000 + i,
                userId: 1001 + i,
                ruleName: `High Alerts ${i + 1}`,
                description: `level >= ${i % 4}`,
                deviceKeys: `dev-${i + 1},dev-${i + 2}`,
                eventLevels: `${(i % 4) + 1},${(i % 3) + 2}`,
                cooldownMin: 5 + i,
                template:
                  "Alert ${eventName} on ${deviceKey}, levels ${eventLevels}",
                recipientIds: "1,2,3",
                enabled: i % 2,
                createdAt: 1764901172000 + i * 1000,
                updatedAt: 1764901172000 + i * 1000,
              }))
            ),
          2000
        )
      );
    },
    async handleCommand({ command, item }) {
      if (command === "delete") {
        await this.handleDelete(item);
      } else if (command === "edit") {
        await this.handleEditModel(item);
      } else if (command === "view") {
        await this.handleViewDetail(item);
      } else if (command === "test") {
        await this.handleTest(item);
      }
    },
    async handleDelete(item) {
      const me = this;
      await me.$confirm(
        `Are you sure you want to delete rule ${item?.name}?`,
        "Warning",
        {
          type: "warning",
        }
      );
      await deleteNotificationRule(item.id);
      me.tableCtrl.refresh();
    },
    async handleEditModel(item) {
      const me = this;
      const result = await me.$loaderWindow({
        title: `Edit Alert Rule`,
        component: () => import("./EditWhatsAppAlert.vue"),
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: { id: item.id },
      });
      console.log("Edit result:", result);
      await me.tableCtrl.refresh();
      me.$message.success(
        `Notification rule ${item?.id ? "updated" : "created"} successfully!`
      );
    },
    async handleViewDetail(item) {
      await this.$loaderWindow({
        title: `View Alert Rule`,
        component: () => import("./DetailWhatsAppAlert.vue"),
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: { id: item.id },
      });
    },
    async handleTest(item) {
      const params = {
        ruleId: item.id,
      };
      const loading = Loading.service({
        lock: true,
        text: "Sending test notification...",
        background: "rgba(0, 0, 0, 0.4)",
      });
      try {
        const response = await testNotificationTemplate(params);
        const html = this.formatTestResult(response);
        await this.$alert(html, "Test Notification Result", {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "OK",
        });
      } catch (error) {
        const message =
          (error && error.message) ||
          (error && error.msg) ||
          "Failed to send test notification.";
        this.$message.error(message);
      } finally {
        loading.close();
      }
    },
    formatTestResult(result) {
      if (!result) {
        return "<p>No response data.</p>";
      }
      if (Array.isArray(result.messages)) {
        const rows = result.messages
          .map(
            (msg, index) =>
              `<div style="padding:8px 0;border-bottom:1px solid rgba(148,163,184,0.3);">
                <div><strong>#${index + 1}</strong> · ${
                  msg.recipientName || msg.recipientId || "Recipient"
                }</div>
                <div>Status: ${msg.status || "--"} (${msg.success ? "success" : "failed"})</div>
                <div style="margin-top:4px;color:#94a3b8;">${
                  msg.message || "--"
                }</div>
              </div>`
          )
          .join("");
        return `
          <div>
            <p><strong>Rule:</strong> ${result.ruleName || result.ruleId || "--"}</p>
            <div style="margin-top:8px;">${rows}</div>
          </div>`;
      }
      return `
        <div>
          <p><strong>Recipient:</strong> ${
            result.recipientName || result.recipientId || "--"
          }</p>
          <p><strong>Status:</strong> ${result.status || "--"} (${
        result.success ? "success" : "failed"
      })</p>
          <div style="margin-top:8px;color:#94a3b8;">${
            result.message || "--"
          }</div>
        </div>`;
    },
    refreshList() {
      this.tableCtrl.refresh();
    },
  },
};
</script>
